/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */

import { Box, Button, chakra, Flex } from "@chakra-ui/react"
import { useEffect, useState } from "react"
import SwiperCore, { Navigation, Scrollbar, Thumbs } from "swiper"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { ImageModel } from "../../../../api-clients/product-api-v1"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import { handleDownloadClick } from "../../../../common/utils/common-utils"
import { sortImages } from "../../../../common/utils/pdp-utils"
import CustomImage from "../../../shared/CustomImage"
import CustomTooltip from "../../../shared/CustomTooltip"
import DownloadButton from "../../../shared/DownloadButton/DownloadButton"
import { CChevronLeft, CChevronRight, CMinus } from "../../../shared/Icons"
import { GalleryNavigationButton } from "../../series/SeriesDetailGallery/GalleryNavigationButton"

type Props = {
  images: ImageModel[]
  activeSlide: number
  selectedArticleInd: number
  isArchived?: boolean
}

// Install Swiper modules
SwiperCore.use([Thumbs, Navigation, Scrollbar])

const ChakraSwiper = chakra(Swiper)
const ChakraSwiperSlide = chakra(SwiperSlide)
ChakraSwiperSlide.displayName = "SwiperSlide"

export function GalleryInModal({ images, activeSlide, selectedArticleInd, isArchived }: Props) {
  const { t } = useBrandedTranslation()
  const fallbackImgSrc = [{ large: "/images/placeholder-image.svg", thumbnail: "/images/placeholder-image.svg" }]
  images = images && images.length ? sortImages(images, selectedArticleInd != 0 || isArchived) : fallbackImgSrc

  //https://github.com/nolimits4web/swiper/issues/3855
  // Main swiper navigation buttons
  const [prevElMain, setPrevElMain] = useState<HTMLElement | null>(null)
  const [nextElMain, setNextElMain] = useState<HTMLElement | null>(null)

  const [mainSwiper, setMainSwiper] = useState<SwiperCore | null>(null)

  const [activeThumbId, setActiveThumbId] = useState<number>(activeSlide)
  const [showAll, setShowAll] = useState<boolean>(false)
  const numberOfVisibleThumbs = 17 // TODO (vva) change this whem we have the dynamic hiding from the anchor navigation

  useEffect(() => {
    if (selectedArticleInd != 0) {
      setActiveThumbId(0)
      mainSwiper?.slideTo(0)
    }
  }, [selectedArticleInd])

  return (
    <Flex gap="6" alignItems="center" flexDirection="column" mt="4">
      <Flex mb="12" gap="4" maxW={{ base: "161.5", md: "178" }} alignItems="center" position="relative">
        <GalleryNavigationButton
          icon={<CChevronLeft />}
          ariaLabel="Show the previous slide"
          setRef={setPrevElMain}
          height={{ base: "131.5", md: "150" }}
        />
        <ChakraSwiper
          onSwiper={setMainSwiper}
          onSlideChange={(swiper) => setActiveThumbId(swiper.activeIndex)}
          zIndex="base"
          loop={false}
          spaceBetween={120} // value is in px
          watchOverflow={true}
          grabCursor={true}
          initialSlide={activeSlide}
          navigation={{ prevEl: prevElMain, nextEl: nextElMain }}
        >
          {images?.map((img) => (
            <ChakraSwiperSlide key={img.key} display="flex" alignItems="center" justifyContent="center">
              <CustomImage
                src={img?.large}
                alt="Main gallery image"
                maxWidth={{ base: "131.5", md: "150" }}
                maxHeight={{ base: "131.5", md: "150" }}
                w={{ base: "131.5", md: "150" }}
                h={{ base: "131.5", md: "150" }}
                objectFit="contain"
              />
            </ChakraSwiperSlide>
          ))}
        </ChakraSwiper>

        <GalleryNavigationButton
          icon={<CChevronRight />}
          ariaLabel="Show the next slide"
          setRef={setNextElMain}
          height={{ base: "131.5", md: "150" }}
        />
        <CustomTooltip label={t("pdp-article-more-pic")} maxW="66">
          <Box position="absolute" bottom="0px" right="14">
            <DownloadButton
              aria-label="Download the image"
              onClick={(e) => handleDownloadClick(e, images[mainSwiper?.activeIndex]?.original)}
            />
          </Box>
        </CustomTooltip>
      </Flex>

      <Flex gap="2" width="full" flexWrap="wrap">
        {images.slice(0, showAll ? images.length : numberOfVisibleThumbs).map((img, i) => {
          return (
            <Box
              key={img.key}
              p="1"
              w="10"
              h="10"
              boxSizing="content-box"
              border="1px solid"
              borderColor={activeThumbId == i ? "black" : "gray.300"}
              _hover={{ borderColor: activeThumbId == i ? "black" : "gray.500" }}
              cursor="pointer"
              onClick={() => mainSwiper?.slideTo(i)}
            >
              <CustomImage src={img?.thumbnail} alt="Thumbnail image" objectFit="contain" w="10" h="10" />
            </Box>
          )
        })}
        {images.length > numberOfVisibleThumbs && !showAll && (
          <CustomTooltip label={t("show-more")}>
            <Button w="12.5" h="12.5" fontSize="2xs" fontWeight="normal" onClick={() => setShowAll(true)}>
              +{images.length - numberOfVisibleThumbs}
            </Button>
          </CustomTooltip>
        )}
      </Flex>

      {showAll && (
        <Flex w="100%">
          <Button
            bgColor="transparent"
            border="1px solid"
            leftIcon={<CMinus />}
            fontSize="sm"
            fontWeight="medium"
            onClick={() => setShowAll(false)}
          >
            {t("show-less")}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}
